import React, { useEffect, useState } from 'react';
import { View } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { checkSystemHealth } from '../services/geminiService';
import { 
  Search, PenTool, LayoutDashboard, Activity, Wrench, 
  Zap, Swords, Wallet, HardDrive, Shield, LogOut, 
  ArrowRight, Command, Server, CheckCircle, AlertTriangle, UsersRound
} from 'lucide-react';

interface CommandPaletteProps {
  setView: (view: View) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ setView }) => {
  const { logout, isAdmin } = useAuth();
  const { success, error, info } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [checking, setChecking] = useState(false);

  // Define commands
  const commands = [
    { 
      id: 'goto-dashboard', 
      label: 'Đến trang Tổng quan', 
      icon: LayoutDashboard, 
      action: () => setView(View.DASHBOARD),
      group: 'Navigation'
    },
    { 
      id: 'goto-keyword-research', 
      label: 'Nghiên cứu & Phân tích từ khóa', 
      icon: Search, 
      action: () => setView(View.KEYWORD_RESEARCH),
      group: 'Navigation'
    },
    { 
      id: 'goto-content-planner', 
      label: 'Chiến lược & Kế hoạch (Topic Clusters)', 
      icon: LayoutDashboard, 
      action: () => setView(View.CONTENT_PLANNER),
      group: 'Navigation'
    },
    { 
      id: 'goto-writer', 
      label: 'Viết bài mới (Writer)', 
      icon: PenTool, 
      action: () => setView(View.WRITER),
      group: 'Navigation'
    },
    { 
      id: 'goto-competitor', 
      label: 'Phân tích đối thủ', 
      icon: Swords, 
      action: () => setView(View.COMPETITOR),
      group: 'Navigation'
    },
    { 
      id: 'goto-content-gap', 
      label: 'Phân tích Lỗ hổng Nội dung', 
      icon: UsersRound, 
      action: () => setView(View.CONTENT_GAP),
      group: 'Navigation'
    },
    { 
      id: 'goto-audit', 
      label: 'Kiểm toán nội dung', 
      icon: Activity, 
      action: () => setView(View.AUDIT),
      group: 'Navigation'
    },
    { 
      id: 'goto-library', 
      label: 'Thư viện bài viết', 
      icon: HardDrive, 
      action: () => setView(View.LIBRARY),
      group: 'Navigation'
    },
    { 
      id: 'goto-tools', 
      label: 'Tiện ích SEO', 
      icon: Wrench, 
      action: () => setView(View.TOOLS),
      group: 'Navigation'
    },
    { 
      id: 'goto-wallet', 
      label: 'Nạp tiền / Ví', 
      icon: Wallet, 
      action: () => setView(View.WALLET),
      group: 'Navigation'
    },
    { 
      id: 'check-system', 
      label: 'Kiểm tra trạng thái hệ thống', 
      icon: Server, 
      action: async () => {
          setChecking(true);
          info("Đang kiểm tra kết nối Server...");
          try {
            const res = await checkSystemHealth();
            if (res.status === 'ok') {
                success(`Hệ thống ổn định!`, `Gemini API Latency: ${res.latency}ms. ${res.message}`);
            } else {
                error("Lỗi kết nối!", res.message);
            }
          } catch (e) {
            error("Critical Error", "Không thể kết nối đến Backend.");
          } finally {
            setChecking(false);
            setIsOpen(false);
          }
      },
      group: 'System'
    },
    { 
      id: 'action-logout', 
      label: 'Đăng xuất', 
      icon: LogOut, 
      action: () => logout(),
      group: 'Account'
    }
  ];

  if (isAdmin) {
    commands.splice(7, 0, {
      id: 'goto-autowriter',
      label: 'AI Gen Write (Auto)',
      icon: Zap,
      action: () => setView(View.AUTO_WRITER),
      group: 'Admin'
    });
    commands.splice(8, 0, {
      id: 'goto-admin',
      label: 'Quản trị hệ thống',
      icon: Shield,
      action: () => setView(View.ADMIN_DASHBOARD),
      group: 'Admin'
    });
  }

  // Filter commands
  const filteredCommands = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(query.toLowerCase())
  );

  // Handle Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle Ctrl+K or Cmd+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setQuery('');
        setSelectedIndex(0);
      }

      if (!isOpen) return;

      if (e.key === 'Escape') {
        setIsOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
          // Don't close immediately if action is async like system check, handled inside action
          if (filteredCommands[selectedIndex].id !== 'check-system') {
             setIsOpen(false);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={() => setIsOpen(false)}
      ></div>

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-[#1c1917] rounded-2xl shadow-2xl border border-white/10 overflow-hidden text-stone-200 animate-slide-up transform transition-all">
        {/* Header Input */}
        <div className="flex items-center px-4 border-b border-white/10 relative">
          <Search className="w-5 h-5 text-stone-500 mr-3" />
          <input
            type="text"
            className="w-full bg-transparent py-5 text-sm font-medium text-white placeholder:text-stone-500 focus:outline-none"
            placeholder="Tìm kiếm lệnh hoặc tính năng..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            autoFocus
          />
          <div className="hidden sm:flex items-center gap-1 text-[10px] font-bold text-stone-500 bg-white/5 px-2 py-1 rounded-lg border border-white/5">
            <span>ESC</span>
          </div>
          {checking && <div className="absolute bottom-0 left-0 h-0.5 bg-brand-500 animate-loading-bar"></div>}
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2 custom-scrollbar">
          {filteredCommands.length > 0 ? (
            <div className="space-y-1 px-2">
              {filteredCommands.map((cmd, idx) => (
                <button
                  key={cmd.id}
                  onClick={() => {
                    cmd.action();
                    if (cmd.id !== 'check-system') setIsOpen(false);
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all group ${
                    idx === selectedIndex 
                      ? 'bg-brand-600 text-white shadow-lg shadow-brand-900/20' 
                      : 'text-stone-400 hover:bg-white/5 hover:text-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <cmd.icon size={18} className={idx === selectedIndex ? 'text-white' : 'text-stone-500 group-hover:text-stone-300'} />
                    <span className="font-medium">{cmd.label}</span>
                  </div>
                  {idx === selectedIndex && <ArrowRight size={14} className="animate-pulse" />}
                </button>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-stone-500 text-sm">
              Không tìm thấy kết quả cho "{query}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#151413] px-4 py-3 border-t border-white/5 flex items-center justify-between text-[10px] text-stone-600 font-medium">
          <div className="flex gap-4">
            <span><strong className="text-stone-500">↑↓</strong> điều hướng</span>
            <span><strong className="text-stone-500">↵</strong> chọn</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Command size={12} /> 
            <span>SEO Master Command v2.6</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;